import { computePosition, flip, offset, shift } from "@floating-ui/dom";
import { getExtensionDependencyFromEditor } from "@lexical/extension";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COPY_COMMAND,
  type NodeKey,
} from "lexical";
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type JSX,
  type MouseEvent,
} from "react";
import { $isListItemNode } from "@lexical/list";
import { $findMatchingParent } from "@lexical/utils";
import { $rootBlockOf } from "../features/block-id/operations";
import { $getBlockId } from "../features/block-id/state";
import { $selectedBlocks, BlockSelectionExtension } from "../features/block-selection/extension";
import { CORE_REGISTRY, type BlockId, type BlockType, type EditorPreset } from "../index";
import type { EditorIntent, DispatchResult } from "./handle";
import type { EditorLabels } from "./labels";
import { keepIfSame, keepListIfSame, perFrame } from "./measure";
import { useBlockDrag } from "./useBlockDrag";

export interface BlockControlsProps {
  labels: EditorLabels;
  preset: EditorPreset;
  dispatch: (intent: EditorIntent) => DispatchResult;
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const px = (value: string): number => Number.parseFloat(value) || 0;

/** The box of the first character in a block, or `null` for a block with no text in it. */
function firstGlyphOf(element: HTMLElement): DOMRect | null {
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const range = element.ownerDocument.createRange();
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if ((node as Text).length === 0) continue;
    range.setStart(node, 0);
    range.setEnd(node, 1);
    const rect = range.getClientRects()[0];
    if (rect !== undefined && rect.height > 0) return rect;
  }
  return null;
}

/**
 * Where the handle goes to be level with a block's first line: as tall as a line of that block and
 * centered on its first character. That is measured, not worked out from styles, because the line
 * is not always where the styles say: Firefox makes a list item's first line taller than its
 * `line-height` to fit the marker. A block with no text (an empty paragraph, a divider) falls back
 * to its styles: inside its padding, and centered on itself when it is shorter than a line.
 */
function firstLineOf(element: HTMLElement): { top: number; height: number } {
  const style = getComputedStyle(element);
  // `normal` has no length; 1.2 is about what browsers make of it.
  const line = px(style.lineHeight) || px(style.fontSize) * 1.2;
  const glyph = firstGlyphOf(element);
  if (glyph !== null) return { top: glyph.top + (glyph.height - line) / 2, height: line };
  const rect = element.getBoundingClientRect();
  const inset = px(style.paddingTop) + px(style.borderTopWidth);
  const content = rect.height - inset - px(style.paddingBottom) - px(style.borderBottomWidth);
  return { top: rect.top + inset + (Math.min(line, content) - line) / 2, height: line };
}

// Controls act on the editor's selection, so pressing one must not take focus from it.
const keepSelection = (event: MouseEvent): void => event.preventDefault();

/**
 * The handle, the highlight of selected blocks, and the actions on them. All of it is drawn in a
 * layer over the editor from measured geometry: Lexical owns the content's DOM, and nothing here
 * adds a class, an attribute, or a wrapper to it. Only the hovered block and the selected ones are
 * ever measured, never the document.
 */
export function BlockControls({ labels, preset, dispatch }: BlockControlsProps): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const selection = getExtensionDependencyFromEditor(editor, BlockSelectionExtension).output;
  const { selected } = useSyncExternalStore(
    selection.subscribe,
    selection.getState,
    selection.getState,
  );
  const layerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<NodeKey | null>(null);
  const [caret, setCaret] = useState<NodeKey | null>(null);
  const [handle, setHandle] = useState<{
    top: number;
    height: number;
    key: NodeKey;
    blockId: BlockId;
    rootId: BlockId;
  } | null>(null);
  const [highlights, setHighlights] = useState<Box[]>([]);
  const active = hovered ?? caret;
  const drag = useBlockDrag(editor, layerRef, dispatch);
  const dragging = drag.feedback !== null;
  // Read by listeners registered once; written after render, where refs may be written.
  const draggingRef = useRef(false);
  useEffect(() => {
    draggingRef.current = dragging;
  }, [dragging]);

  // Which root-level block the pointer is over. One hit test per move, never a scan.
  useEffect(() => {
    const root = editor.getRootElement();
    const container = root?.closest<HTMLElement>(".ze") ?? null;
    if (root === null || container === null) return;
    let last: Element | null = null;
    const onMove = (event: globalThis.MouseEvent): void => {
      // The handle stays with the block being dragged, whatever the pointer passes over.
      if (draggingRef.current) return;
      if (layerRef.current?.contains(event.target as Node) === true) return;
      // In the gutter the pointer is over the root itself: look across to the content.
      const probe =
        event.target === root
          ? document.elementFromPoint(root.getBoundingClientRect().right - 8, event.clientY)
          : (event.target as Element | null);
      // A list item has a handle of its own, so it can be reordered within its list. The item that
      // only wraps a nested list does not: it belongs to the item above it.
      let block = probe;
      while (
        block !== null &&
        block.parentElement !== root &&
        !(block.tagName === "LI" && block.firstElementChild?.matches("ul, ol") !== true)
      ) {
        block = block.parentElement;
      }
      if (block === last) return;
      last = block;
      if (block === null) return setHovered(null);
      const found = block;
      setHovered(editor.read(() => $getNearestNodeFromDOMNode(found)?.getKey() ?? null));
    };
    const onLeave = (): void => {
      if (draggingRef.current) return;
      last = null;
      setHovered(null);
    };
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);
    return () => {
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
    };
  }, [editor]);

  // Which block the caret is in, so the handle is there for the keyboard as well as the pointer.
  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        setCaret(
          editorState.read(() => {
            const current = $getSelection();
            if (!$isRangeSelection(current)) return null;
            const anchor = current.anchor.getNode();
            const item = $findMatchingParent(
              anchor,
              (node) => $isListItemNode(node) && node.getFirstChild()?.getType() !== "list",
            );
            // From inside a table the handle is the table's: a cell is not a block of its own.
            return (item ?? $rootBlockOf(anchor))?.getKey() ?? null;
          }),
        );
      }),
    [editor],
  );

  // Geometry for the handle and the highlights, relative to the layer.
  useEffect(() => {
    const measure = (): void => {
      const layer = layerRef.current;
      if (layer === null) return;
      const base = layer.getBoundingClientRect();
      const boxOf = (key: NodeKey): Box | null => {
        const element = editor.getElementByKey(key);
        if (element === null) return null;
        const rect = element.getBoundingClientRect();
        return {
          top: rect.top - base.top,
          left: rect.left - base.left,
          width: rect.width,
          height: rect.height,
        };
      };
      const described = editor.read(() => {
        const node = active === null ? null : $getNodeByKey(active);
        const blockId = node === null ? null : $getBlockId(node);
        const top = node === null ? null : $rootBlockOf(node);
        const rootId = top === null ? null : $getBlockId(top);
        return {
          handle: blockId === null || rootId === null ? null : { blockId, rootId },
          selected: ($selectedBlocks() ?? []).map((block) => block.getKey()),
        };
      });
      const element = active === null ? null : editor.getElementByKey(active);
      // Unchanged geometry keeps the current state, so a keystroke that moves nothing, or a scroll
      // somewhere else on the page, does not re-render the controls.
      if (element === null || active === null || described.handle === null) setHandle(null);
      else {
        const line = firstLineOf(element);
        setHandle(
          keepIfSame({
            top: line.top - base.top,
            height: line.height,
            key: active,
            ...described.handle,
          }),
        );
      }
      setHighlights(
        keepListIfSame(described.selected.map(boxOf).filter((entry) => entry !== null)),
      );
    };
    measure();
    // Updates measure at once, so the handle is in place in the same frame as the text it follows.
    // Scroll and resize fire faster than frames and only need the last position.
    const frame = perFrame(measure);
    const offUpdate = editor.registerUpdateListener(measure);
    window.addEventListener("resize", frame.schedule);
    window.addEventListener("scroll", frame.schedule, { capture: true, passive: true });
    return () => {
      offUpdate();
      frame.cancel();
      window.removeEventListener("resize", frame.schedule);
      window.removeEventListener("scroll", frame.schedule, { capture: true });
    };
  }, [editor, active, selected]);

  // The toolbar goes where there is no content to cover: beside the selection when the layout has
  // room there, which a fixed text measure usually leaves. Otherwise above or below the whole
  // selection, at the far end of the line, never on top of a selected block. Text next to a
  // selection has to stay clickable: clicking it is how block selection is left.
  const first = highlights[0];
  const last = highlights[highlights.length - 1];
  const open = selected.length > 0 && first !== undefined && last !== undefined;
  const top = first?.top ?? 0;
  const left = first?.left ?? 0;
  const width = first?.width ?? 0;
  const bottom = last === undefined ? 0 : last.top + last.height;
  useEffect(() => {
    const toolbar = toolbarRef.current;
    const layer = layerRef.current;
    if (!open || toolbar === null || layer === null) return;
    const base = layer.getBoundingClientRect();
    const rect = new DOMRect(base.left + left, base.top + top, width, bottom - top);
    void computePosition({ getBoundingClientRect: () => rect, contextElement: layer }, toolbar, {
      placement: "right-start",
      strategy: "fixed",
      middleware: [
        offset(8),
        flip({ padding: 8, fallbackPlacements: ["top-end", "bottom-end"] }),
        shift({ padding: 8, crossAxis: false }),
      ],
    }).then(({ x, y }) => {
      toolbar.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
      toolbar.dataset.placed = "true";
    });
  }, [open, top, left, width, bottom]);

  const act = (intent: EditorIntent, reselect: boolean): void => {
    const ids = [...selected];
    dispatch(intent);
    // A conversion replaces the nodes. The IDs are what survives, so select by them again.
    if (reselect) selection.select(ids);
  };

  const isSelected = handle !== null && selected.includes(handle.rootId);
  const count = selected.length;

  return (
    <div ref={layerRef} className="ze-block-layer">
      {highlights.map((box, index) => (
        // Positional: a highlight has no identity of its own.
        // oxlint-disable-next-line react/no-array-index-key
        <div key={index} className="ze-block-highlight" style={box} />
      ))}
      {handle !== null && (
        <button
          type="button"
          className="ze-block-handle"
          style={{ top: handle.top, height: handle.height }}
          aria-label={labels.selectBlock}
          aria-pressed={isSelected}
          data-block-id={handle.blockId}
          data-dragging={dragging}
          onMouseDown={keepSelection}
          {...drag.handleProps(handle.key)}
          onClick={(event) => {
            // The press that ends a drag is not a click on the handle.
            if (drag.consumeClick()) return;
            // Selection is root-level: a list item's handle selects the list it is in.
            selection.selectBlock(handle.rootId, event.shiftKey);
            // From the keyboard the button has focus; the arrow keys that extend the selection
            // are the editor's, so give it back.
            editor.getRootElement()?.focus({ preventScroll: true });
          }}
        >
          <span aria-hidden="true">⋮⋮</span>
        </button>
      )}
      {drag.feedback !== null && drag.feedback.line !== null && (
        <div className="ze-drop-line" style={drag.feedback.line} />
      )}
      {drag.feedback !== null && (
        <div
          className="ze-drag-preview"
          data-valid={drag.feedback.valid}
          style={{
            transform: `translate(${drag.feedback.preview.x + 12}px, ${drag.feedback.preview.y + 12}px)`,
          }}
        >
          {drag.feedback.label === "" ? labels.dragEmptyBlock : drag.feedback.label}
          {drag.feedback.count > 1 && (
            <span className="ze-drag-count">+{drag.feedback.count - 1}</span>
          )}
        </div>
      )}
      {/* Says what is selected once, when it changes, rather than per key. */}
      <output className="ze-visually-hidden">
        {count === 0 ? "" : labels.blocksSelected.replace("{count}", String(count))}
      </output>
      {open && (
        <div
          ref={toolbarRef}
          className="ze-toolbar ze-block-toolbar"
          role="toolbar"
          tabIndex={-1}
          aria-label={labels.blockActions}
          onMouseDown={(event) => {
            // The menu needs its own focus to open; every button keeps the editor's.
            if (!(event.target instanceof HTMLSelectElement)) event.preventDefault();
          }}
        >
          <select
            className="ze-block-turn-into"
            aria-label={labels.turnInto}
            value=""
            onChange={(event) =>
              act(
                { type: "turn-into", blockIds: selected, block: event.target.value as BlockType },
                true,
              )
            }
          >
            <option value="">{labels.turnInto}</option>
            {CORE_REGISTRY.commandsFor(preset).map((command) =>
              command.action === "turn-into" && command.secondary !== true ? (
                <option key={command.id} value={command.block}>
                  {command.label}
                </option>
              ) : null,
            )}
          </select>
          <button
            type="button"
            className="ze-tb-button"
            aria-label={labels.moveUp}
            onClick={() =>
              act({ type: "move-blocks-by", blockIds: selected, direction: "up" }, false)
            }
          >
            ↑
          </button>
          <button
            type="button"
            className="ze-tb-button"
            aria-label={labels.moveDown}
            onClick={() =>
              act({ type: "move-blocks-by", blockIds: selected, direction: "down" }, false)
            }
          >
            ↓
          </button>
          <button
            type="button"
            className="ze-tb-button"
            onClick={() => act({ type: "duplicate-blocks", blockIds: selected }, false)}
          >
            {labels.duplicate}
          </button>
          <button
            type="button"
            className="ze-tb-button"
            onClick={() => editor.dispatchCommand(COPY_COMMAND, null)}
          >
            {labels.copy}
          </button>
          <button
            type="button"
            className="ze-tb-button"
            onClick={() => {
              editor.dispatchCommand(COPY_COMMAND, null);
              act({ type: "delete-blocks", blockIds: selected }, false);
            }}
          >
            {labels.cut}
          </button>
          <button
            type="button"
            className="ze-tb-button"
            onClick={() => act({ type: "delete-blocks", blockIds: selected }, false)}
          >
            {labels.deleteBlocks}
          </button>
        </div>
      )}
    </div>
  );
}
